import React, { useState, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Play, FileText, Image, Globe, Rocket, RefreshCw } from "lucide-react";
import { useSiteContext } from "../../context/SiteContext";
import { usePubSub } from "@videosdk.live/react-sdk";
import folderService from "../../services/folderServices";
import { toast } from "react-toastify";
import { VerticalTemplates } from "../../services/Templates";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { sideBarModes } from "../../utils/common";
import pollServices from "../../services/pollServices";
import logger from '../../utils/logger';

const normalizePollForClient = (poll) => {
  if (!poll || typeof poll !== "object") {
    return null;
  }

  const votes = Array.isArray(poll.votes)
    ? poll.votes
        .map((vote) => ({
          ...vote,
          optionIndices: Array.isArray(vote?.optionIndices)
            ? vote.optionIndices
            : [],
        }))
        .filter((vote) => vote.participantId)
    : [];

  const skipped = Array.isArray(poll.skipped)
    ? poll.skipped
        .map((entry) => ({
          participantId: entry?.participantId,
          participantName:
            entry?.participantName ||
            entry?.name ||
            entry?.displayName ||
            "Anonymous",
          timestamp: entry?.timestamp || Date.now(),
        }))
        .filter((entry) => entry.participantId)
    : [];

  const options = (poll.options || []).map((option, index) => {
    const resolvedIndex =
      typeof option === "object" &&
      option !== null &&
      typeof option.index === "number"
        ? option.index
        : index;

    if (typeof option === "string") {
      return {
        text: option,
        index: resolvedIndex,
        votes: votes.filter((vote) =>
          vote.optionIndices?.includes(resolvedIndex)
        ).length,
      };
    }

    if (option && typeof option === "object") {
      const resolvedText =
        option.text ||
        option.label ||
        option.value ||
        `Option ${resolvedIndex + 1}`;

      return {
        ...option,
        index: resolvedIndex,
        text: resolvedText,
        votes: votes.filter((vote) =>
          vote.optionIndices?.includes(resolvedIndex)
        ).length,
      };
    }

    const value = option ?? `Option ${resolvedIndex + 1}`;
    return {
      text: String(value),
      index: resolvedIndex,
      votes: votes.filter((vote) => vote.optionIndices?.includes(resolvedIndex))
        .length,
    };
  });

  const sanitizedPoll = {
    ...poll,
    options,
    votes,
    skipped,
    totalVotes: votes.length,
    skippedCount: skipped.length,
    responseCount: votes.length + skipped.length,
  };

  if (!sanitizedPoll.id) {
    sanitizedPoll.id = poll.pollId || poll._id || `${Date.now()}`;
  }

  sanitizedPoll.lastUpdated = Date.now();

  return sanitizedPoll;
};

const ElementSection = ({ getFolderElementList, isRefreshing }) => {
  const {
    folderElementList,
    setFolderElementList,
    setActiveElement,
    setShowElementSection,
    prompterElement,
    setPrompterElement,
    user,
    meetingRole,
    setPollResults,
    setShowPollResults,
  } = useSiteContext();
  const { setSideBarMode, setLeftSideBarMode } = useMeetingAppContext();
  const { publish } = usePubSub("SHOW_ELEMENT_SECTION");
  const { publish: publishPoll } = usePubSub("POLL");

  const [polls, setPolls] = useState([]);
  const [loadingPolls, setLoadingPolls] = useState(false);
  const [showPolls, setShowPolls] = useState(false);

  useEffect(() => {
    if (
      user?.plan === "Business" &&
      ["moderator", "co-host"].includes(meetingRole)
    ) {
      fetchPolls();
    }
  }, [user?.plan, meetingRole]);

  const fetchPolls = async () => {
    setLoadingPolls(true);
    try {
      const response = await pollServices.getUserPolls();
      if (response) {
        if (response.success && response.data) {
          setPolls(Array.isArray(response.data) ? response.data : []);
        } else if (Array.isArray(response)) {
          setPolls(response);
        } else if (response.data && Array.isArray(response.data)) {
          setPolls(response.data);
        } else {
          setPolls([]);
        }
      } else {
        setPolls([]);
      }
    } catch (error) {
      logger("error", "Error fetching polls:", error);
      setPolls([]);
    } finally {
      setLoadingPolls(false);
    }
  };

  const handleLaunchPoll = (poll) => {
    try {
      const pollPayload = normalizePollForClient({
        ...poll,
        votes: [],
        skipped: [],
        totalVotes: 0,
      });

      if (!pollPayload) {
        toast.error("Unable to launch poll. Poll data is invalid.");
        return;
      }

      setShowElementSection(false);
      setPollResults(pollPayload);
      setShowPollResults(true);
      publishPoll(
        JSON.stringify({
          type: "launch",
          poll: pollPayload,
          timestamp: Date.now(),
        })
      );
      toast.success("Poll launched successfully!");
    } catch (error) {
      logger("error", "Error launching poll:", error);
      toast.error("Failed to launch poll. Please try again.");
    }
  };

  const shouldShowElement = (elementType) => {
    if (user?.plan == "Business") {
      return true;
    }
    return (
      elementType === "document" ||
      elementType === "video" ||
      elementType === "audio"
    );
  };

  const getPreviewImage = (elm) => {
    if (elm.type === "cta") {
      const displayStyle = elm.displayStyle || "";
      const settings = elm.settings
        ? typeof elm.settings === "string"
          ? JSON.parse(elm.settings)
          : elm.settings
        : {};
      const placement = settings.placement || "center";

      if (displayStyle.startsWith("vertical")) {
        return placement === "left"
          ? "/icons/BannerLeft.svg"
          : "/icons/BannerRight.svg";
      } else if (displayStyle.startsWith("horizontal")) {
        return placement === "top"
          ? "/icons/BannerTop.svg"
          : "/icons/BannerBottom.svg";
      } else if (displayStyle.startsWith("centered")) {
        return "/icons/BannerCentered.svg";
      }

      return (
        VerticalTemplates[displayStyle]?.previewImage ||
        "/icons/BannerCentered.svg"
      );
    } else if (elm.type === "prompter") {
      return "/icons/captions.svg";
    }
    return "/icons/BannerCentered.svg";
  };

  return (
    <div className="h-full bg-black p-6 text-white">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <svg
            width="31"
            height="31"
            viewBox="0 0 31 31"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M29.6816 28.7352V21.9552C29.6816 21.5851 29.3817 21.2852 29.0116 21.2852H11.9516C11.5816 21.2852 11.2816 21.5851 11.2816 21.9552V28.7352C11.2816 29.1052 11.5816 29.4052 11.9516 29.4052H29.0116C29.3817 29.4052 29.6816 29.1052 29.6816 28.7352Z"
              stroke="white"
              stroke-width="1.36"
              stroke-miterlimit="10"
            />
            <path
              d="M9.11133 28.7351L9.11133 1.37509C9.11133 1.00506 8.81136 0.705078 8.44133 0.705078L1.67133 0.705078C1.30129 0.705078 1.00133 1.00506 1.00133 1.37509L1.00133 28.7351C1.00133 29.1051 1.30129 29.4051 1.67133 29.4051H8.44133C8.81136 29.4051 9.11133 29.1051 9.11133 28.7351Z"
              stroke="white"
              stroke-width="1.36"
              stroke-miterlimit="10"
            />
            <path
              d="M29.6816 8.15509V1.37509C29.6816 1.00506 29.3817 0.705078 29.0116 0.705078L11.9516 0.705078C11.5816 0.705078 11.2816 1.00506 11.2816 1.37509V8.15509C11.2816 8.52512 11.5816 8.8251 11.9516 8.8251L29.0116 8.8251C29.3817 8.8251 29.6816 8.52512 29.6816 8.15509Z"
              stroke="white"
              stroke-width="1.36"
              stroke-miterlimit="10"
            />
            <path
              d="M19.4004 18.4451V11.6651C19.4004 11.2951 19.1004 10.9951 18.7304 10.9951H11.9604C11.5904 10.9951 11.2904 11.2951 11.2904 11.6651V18.4451C11.2904 18.8151 11.5904 19.1151 11.9604 19.1151H18.7304C19.1004 19.1151 19.4004 18.8151 19.4004 18.4451Z"
              stroke="white"
              stroke-width="1.36"
              stroke-miterlimit="10"
            />
          </svg>

          <h1 className="text-2xl font-bold text-white">ELEMENT</h1>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              getFolderElementList();
              if (
                user?.plan === "Business" &&
                ["moderator", "co-host"].includes(meetingRole)
              ) {
                fetchPolls();
              }
            }}
            disabled={isRefreshing || loadingPolls}
            className="p-2 rounded-lg bg-[#462091] hover:bg-[#5A2AA3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh elements"
          >
            <RefreshCw
              className={`h-5 w-5 text-white ${
                isRefreshing || loadingPolls ? "animate-spin" : ""
              }`}
            />
          </button>
          <button
            onClick={() => setShowElementSection(false)}
            className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white md:hidden"
            title="Close elements"
            aria-label="Close Elements"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* Favorites Section */}
      {/* <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-4">FAVORITES</h2>
        <div className="grid grid-cols-3 gap-4">
          {favorites.map((item) => (
            <div key={item.id} className="cursor-pointer group">
              <div className="aspect-square bg-gray-700 rounded-lg overflow-hidden mb-2 group-hover:bg-gray-600 transition-colors">
                {renderPreview(item)}
              </div>
              <div className="text-center">
                <p className="text-white text-sm font-medium truncate">
                  {item.title}
                </p>
                <p className="text-gray-400 text-xs truncate">{item.preview}</p>
              </div>
            </div>
          ))}
        </div>
      </div> */}

      {/* Separator Line */}
      <div className="border-t border-[#462091] mb-4"></div>

      {/* Categories Section */}
      <div className="space-y-2">
        {/* Polls Section - Only show for Business plan moderators/co-hosts */}
        {user?.plan === "Business" &&
          ["moderator", "co-host"].includes(meetingRole) && (
            <div className="relative">
              <button
                onClick={() => {
                  setShowPolls(!showPolls);
                  if (!showPolls && polls.length === 0 && !loadingPolls) {
                    fetchPolls();
                  }
                }}
                className="w-full bg-[#462091] hover:bg-[#462091] rounded-lg p-4 flex items-center justify-between transition-colors group"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 rounded flex items-center justify-center">
                    <span className="text-black font-bold text-[10px] ml-0 absolute mt-[3px]">
                      P
                    </span>
                    <svg
                      width="100"
                      height="100"
                      viewBox="0 0 19 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.4199 2.85034H9.85059C9.34059 2.85034 8.83012 2.73024 8.37012 2.50024L4.89062 0.760254C4.74062 0.680254 4.57039 0.640137 4.40039 0.640137H2.2002C0.980195 0.640137 0 1.63033 0 2.84033V14.8604C0 15.6304 0.260195 16.3301 0.700195 16.8901C0.700195 16.8901 1.40062 18.1003 2.64062 18.1003C2.86063 18.1403 3.08055 18.1702 3.31055 18.1702H7.33984H15.4307C15.6607 18.1702 15.8806 18.1503 16.1006 18.1003C17.3406 18.1003 18.04 16.8901 18.04 16.8901C18.48 16.3301 18.7402 15.6204 18.7402 14.8604V6.14014C18.7402 4.32014 17.2607 2.83032 15.4307 2.83032L15.4199 2.85034Z"
                        fill="white"
                      />
                    </svg>
                  </div>
                  <span className="text-white font-medium">Polls</span>
                </div>
                <div className="flex items-center space-x-2">
                  <ChevronDownIcon
                    className={`h-5 w-5 text-white transition-transform ${
                      showPolls ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>

              {showPolls && (
                <div className="mt-2 rounded-lg">
                  {loadingPolls ? (
                    <div className="text-white text-center py-4 text-sm">
                      Loading polls...
                    </div>
                  ) : polls.length === 0 ? (
                    <div className="text-gray-400 text-center py-4 text-sm">
                      No polls available
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {polls.map((poll) => (
                        <div
                          key={poll.id || poll._id}
                          onClick={() => handleLaunchPoll(poll)}
                          className="flex flex-col cursor-pointer"
                        >
                          <div className="bg-[#232323] h-[75px] w-full flex items-center justify-center border border-white">
                            <svg
                              className="w-8 h-8 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                              />
                            </svg>
                          </div>
                          <span className="text-white text-xs text-center w-full truncate mt-2 text-nowrap overflow-hidden">
                            {poll.question || "Untitled Poll"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        {folderElementList.map((item) => (
          <div key={item._id} className="relative">
            <button
              onClick={() => {
                // just update the item in the array
                setFolderElementList(
                  folderElementList.map((newitem) =>
                    newitem._id === item._id
                      ? { ...newitem, show: !newitem.show }
                      : newitem
                  )
                );
              }}
              className="w-full bg-[#462091] hover:bg-[#462091] rounded-lg p-4 flex items-center justify-between transition-colors group"
            >
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 rounded flex items-center justify-center">
                  <span className="text-black font-bold text-[10px] ml-0 absolute mt-[3px]">
                    {item.name.charAt(0).toUpperCase()}
                  </span>
                  <svg
                    width="100"
                    height="100"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.4199 2.85034H9.85059C9.34059 2.85034 8.83012 2.73024 8.37012 2.50024L4.89062 0.760254C4.74062 0.680254 4.57039 0.640137 4.40039 0.640137H2.2002C0.980195 0.640137 0 1.63033 0 2.84033V14.8604C0 15.6304 0.260195 16.3301 0.700195 16.8901C0.700195 16.8901 1.40062 18.1003 2.64062 18.1003C2.86063 18.1403 3.08055 18.1702 3.31055 18.1702H7.33984H15.4307C15.6607 18.1702 15.8806 18.1503 16.1006 18.1003C17.3406 18.1003 18.04 16.8901 18.04 16.8901C18.48 16.3301 18.7402 15.6204 18.7402 14.8604V6.14014C18.7402 4.32014 17.2607 2.83032 15.4307 2.83032L15.4199 2.85034Z"
                      fill="white"
                    />
                  </svg>
                </div>
                <span className="text-white font-medium">{item.name}</span>
              </div>
              <div className="flex items-center space-x-2">
                {/* {category.hasTag && (
                  <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xs">T</span>
                  </div>
                )} */}
                <ChevronDownIcon
                  className={`h-5 w-5 text-white transition-transform ${
                    item.show ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>

            {item.show && item.elements && item.elements.length > 0 && (
              <div className="mt-2 rounded-lg">
                <div className="grid grid-cols-4 gap-2">
                  {item.elements
                    .filter((elm) => shouldShowElement(elm.type))
                    .map((elm) => (
                      <>
                        {elm.type === "video" && (
                          <div
                            onClick={() => {
                              // setActiveElement(elm);
                              setShowElementSection(false);
                              publish(JSON.stringify(elm), {
                                persist: true,
                              });
                            }}
                            className="flex flex-col cursor-pointer"
                          >
                            <div className="bg-[#232323] h-[75px] w-full flex items-center justify-center border border-white">
                              <img
                                src="/icons/Video.svg"
                                alt="Video"
                                className="h-full w-full p-4 object-contain"
                              />
                            </div>
                            <span className="text-white text-xs text-center w-full truncate mt-2 text-nowrap overflow-hidden">
                              {elm.name}
                            </span>
                          </div>
                        )}
                        {elm.type === "website" && (
                          <div
                            onClick={() => {
                              // setActiveElement(elm);
                              setShowElementSection(false);
                              publish(JSON.stringify(elm), {
                                persist: true,
                              });
                            }}
                            className="flex flex-col cursor-pointer"
                          >
                            <div className="bg-[#232323] h-[75px] w-full flex items-center justify-center border border-white">
                              <img
                                src="/icons/Websites.svg"
                                alt="Website"
                                className="h-full w-full p-4 object-contain"
                              />
                            </div>
                            <span className="text-white text-xs text-center w-full truncate mt-2 text-nowrap overflow-hidden">
                              {elm.name}
                            </span>
                          </div>
                        )}
                        {elm.type === "cta" && (
                          <div
                            onClick={() => {
                              // setActiveElement(elm);
                              publish(JSON.stringify(elm), {
                                persist: true,
                              });
                              setShowElementSection(false);
                              setLeftSideBarMode(null);
                            }}
                            className="flex flex-col cursor-pointer"
                          >
                            <div className="bg-[#232323] h-[75px] w-full flex items-center justify-center border border-white">
                              <img
                                src={getPreviewImage(elm)}
                                alt="CTA"
                                className="h-full w-full p-4 object-contain"
                              />
                            </div>
                            <span className="text-white text-xs text-center w-full truncate mt-2 text-nowrap">
                              {elm.name}
                            </span>
                          </div>
                        )}

                        {elm.type === "document" && (
                          <div
                            onClick={() => {
                              // setActiveElement(elm);
                              publish(JSON.stringify(elm), {
                                persist: true,
                              });
                              setShowElementSection(false);
                            }}
                            className="flex flex-col cursor-pointer"
                          >
                            <div className="bg-[#232323] h-[75px] w-full flex items-center justify-center border border-white">
                              <img
                                src="/icons/SlidesPDFDOC.svg"
                                alt="Document"
                                className="h-full w-full p-4 object-contain"
                              />
                            </div>
                            <span className="text-white text-xs text-center w-full truncate mt-2 text-nowrap">
                              {elm.name}
                            </span>
                          </div>
                        )}

                        {elm.type === "prompter" && (
                          <div
                            onClick={() => {
                              setPrompterElement(elm);
                              setShowElementSection(false);
                            }}
                            className="flex flex-col cursor-pointer"
                          >
                            <div className="bg-[#232323] h-[75px] w-full flex items-center justify-center border border-white">
                              <img
                                src="/icons/captions.svg"
                                alt="Prompter"
                                className="h-full w-full p-4 object-contain"
                              />
                            </div>
                            <span className="text-white text-xs text-center w-full truncate mt-2 text-nowrap">
                              {elm.name}
                            </span>
                          </div>
                        )}
                      </>
                    ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ElementSection;
